import { useServices, useStores } from 'index';
import './ProfileMenu.scss';
import { useGetProfile } from 'queryHooks/Profile/useGetProfile';
import { useTranslation } from 'react-i18next';
import { getUserInitials } from 'utilities/userInfo';
import { Divider, Menu, MenuItem } from '@trackman/web-shared-components';
import { useState } from 'react';
import { getLanguageText } from 'utilities/languages';
import { useGetLanguages, useGetUserLanguage, useSelectLanguage } from 'queryHooks/language';
import { LanguageType } from 'types/languages';
import { CountryFlag } from 'utilities/CountryFlag';
import { ReactComponent as Profile } from '../../../images/icons/menu/Profile.svg';
import { ReactComponent as SignOut } from '../../../images/icons/menu/Sign_Out.svg';
import { getErrorMessage } from 'utilities/ErrorHelper';
import { clearCartFromLocalStorage } from 'utilities/cartLocalStorageHelper';
import { useNavigate } from 'react-router-dom';
import { DefaultRoutes } from 'Routes';

export const ProfileMenu = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const accountService = useServices().accountService;
  const notificationsStore = useStores().notificationStore;

  const { data: profileData, isPending: isProfileDataPending } = useGetProfile();
  const { data: userLanguage } = useGetUserLanguage();

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
    setMenuOpen(true);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await accountService.logOut();
      localStorage.removeItem('hasAcceptTermsModalBeenDisplayed');
      clearCartFromLocalStorage();
    } catch (error) {
      console.error('Logout failed', error);
      notificationsStore.addToast('error', getErrorMessage(error), 'error');
    }
  };

  const setLanguage = useSelectLanguage();

  const handleLanguageSelection = (languageCode: LanguageType) => {
    if (!setLanguage.isPending) {
      setLanguage.mutate(languageCode);
      closeLanguageMenu();
      closeMenu();
    }
  };

  const handleProfileSettingsSelection = () => {
    closeMenu();
    navigate(DefaultRoutes.Profile);
  };

  const openLanguageMenu = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageMenuAnchor(event.currentTarget);
    setIsLanguageMenuOpen(true);
  };

  const closeLanguageMenu = () => {
    setLanguageMenuAnchor(null);
    setIsLanguageMenuOpen(false);
  };

  return (
    <div className='profile-menu'>
      <div className='profile-menu--icon' onClick={(event) => openMenu(event)}>
        {profileData?.pictureUrl && profileData.pictureUrl !== '/Frontend/Images/no_avatar.svg' ? (
          <img className='profile-picture' src={profileData.pictureUrl} alt={t('ProfilePicture', { ns: 'editProfile' })} />
        ) : (
          <div className='profile-picture-initials'>{profileData && getUserInitials(profileData.firstName, profileData.lastName)}</div>
        )}
      </div>

      {isMenuOpen && (
        <ProfileMenuList
          fullUserName={profileData ? `${profileData.firstName} ${profileData.lastName}` : ''}
          menuAnchor={menuAnchor}
          open={isMenuOpen}
          userLanguage={userLanguage ?? 'en-US'}
          handleClose={closeMenu}
          handleLogout={handleLogout}
          handleLanguageSubmenu={openLanguageMenu}
          handleProfileSettingsSelection={handleProfileSettingsSelection}
        />
      )}

      {isLanguageMenuOpen && (
        <LanguageSubmenu
          menuAnchor={languageMenuAnchor}
          open={languageMenuAnchor !== null}
          userLanguage={userLanguage ?? 'en-US'}
          handleLanguageSelection={handleLanguageSelection}
          handleClose={closeLanguageMenu}
        />
      )}
    </div>
  );
};

type ProfileMenuListProps = {
  fullUserName: string;
  menuAnchor: HTMLElement | null;
  open: boolean;
  userLanguage: LanguageType;
  handleClose: () => void;
  handleLogout: () => void;
  handleLanguageSubmenu: (event: React.MouseEvent<HTMLElement>) => void;
  handleProfileSettingsSelection: () => void;
};

const ProfileMenuList = ({
  fullUserName,
  menuAnchor,
  open,
  userLanguage,
  handleClose,
  handleLogout,
  handleLanguageSubmenu,
  handleProfileSettingsSelection,
}: ProfileMenuListProps) => {
  const { t } = useTranslation();

  return (
    <>
      <Menu
        className='profile-menu-list'
        anchorEl={menuAnchor}
        open={open}
        onClose={handleClose}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        style={{ top: '10px' }}
      >
        <div className='profile-menu-list--user-name'>{fullUserName}</div>
        <Divider />
        <div>
          <MenuItem label={t('Account', { ns: 'general' })} onClick={handleProfileSettingsSelection} StartIcon={() => <Profile />} />
          <MenuItem
            label={getLanguageText(userLanguage ?? 'en-US')}
            onClick={(event) => handleLanguageSubmenu(event)}
            StartIcon={() => <CountryFlag countryCode={userLanguage ?? 'en-US'} />}
          />
          <MenuItem label={t('LogOut', { ns: 'general' })} onClick={handleLogout} StartIcon={() => <SignOut />} />
        </div>
      </Menu>
    </>
  );
};

const LanguageSubmenu = ({
  menuAnchor,
  open,
  userLanguage,
  handleLanguageSelection,
  handleClose,
}: {
  menuAnchor: HTMLElement | null;
  open: boolean;
  userLanguage: LanguageType;
  handleLanguageSelection: (language: LanguageType) => void;
  handleClose: () => void;
}) => {
  const { data: languages } = useGetLanguages();

  return (
    <Menu
      className='language-submenu'
      anchorEl={menuAnchor}
      open={open}
      onClose={handleClose}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      {languages?.map((language) => (
        <MenuItem
          key={language.countryCode}
          label={language.displayName}
          onClick={() => handleLanguageSelection(language.countryCode)}
          selected={userLanguage === language.countryCode}
          StartIcon={() => <CountryFlag countryCode={language.countryCode} />}
        />
      ))}
    </Menu>
  );
};
